import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { generate } from "../../../../services/report";
import { errorMsg, routes } from "../../../../utils/vars";
import { setSelectedTreeViewItem } from "../../../../redux/features/treeViewChangeSelected";
import { setBackButtonRoute } from "../../../../redux/features/flagDetailsBackButtonRoute";
import {
  clearForMonitoring,
  setForMonitoringData,
  setForMonitoringFilters,
  setForMonitoringPage,
  setForMonitoringTake,
  setForMonitoringTotalCount,
  useSelectFiltersForMonitoring,
  useSelectPageForMonitoring,
  useSelectTakeForMonitoring,
  useSelectTotalCountForMonitoring,
} from "../../../../redux/features/reports/forMonitoringSlice";
import { REPORT_TYPES, REPORT_EXPORT_TYPE } from "../../constants";

const stripEmpty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== "" && v !== null && v !== undefined,
    ),
  );

const useForMonitoring = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const page = useSelectPageForMonitoring();
  const take = useSelectTakeForMonitoring();
  const totalCount = useSelectTotalCountForMonitoring();
  const filters = useSelectFiltersForMonitoring();

  const cleanedFilters = stripEmpty(filters);

  const {
    data: response,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["get-for-monitoring", page, take, cleanedFilters],
    queryFn: () =>
      generate(page, take, {
        type: REPORT_TYPES.FOR_MONITORING,
        forExport: false,
        reportExportType: REPORT_EXPORT_TYPE.PDF,
        ...cleanedFilters,
      }),
    refetchOnWindowFocus: false,
    retry: 1,
  });

  useEffect(() => {
    if (response) {
      dispatch(setForMonitoringData(response.data ?? []));
      dispatch(setForMonitoringTotalCount(response.elements ?? 0));
    }
  }, [response, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(errorMsg, { toastId: "for-monitoring-fetch" });
    }
  }, [error]);

  useEffect(() => {
    return () => {
      dispatch(clearForMonitoring());
    };
  }, [dispatch]);

  const handlePageChange = (newPage) => {
    dispatch(setForMonitoringPage(newPage - 1));
  };

  const handleTakeChange = (newTake) => {
    dispatch(setForMonitoringTake(newTake));
    dispatch(setForMonitoringPage(0));
  };

  const handleFilterChange = (partial) => {
    dispatch(setForMonitoringFilters(partial));
  };

  const handleRowClick = (flagId) => {
    dispatch(setSelectedTreeViewItem(3));
    dispatch(setBackButtonRoute(routes.CASES_FOR_MONITORING));
    navigate(`/reports-for-monitoring/flag-details/${flagId}`);
  };

  return {
    page,
    take,
    totalCount,
    filters,
    isFetching,
    hasError: Boolean(error),
    handlePageChange,
    handleTakeChange,
    handleFilterChange,
    handleRowClick,
  };
};

export default useForMonitoring;
